import type { LegalPage, LanguageKey } from "@akina/domain";
import { useCmsCopyStore } from "@akina/presentation";
import { useQuery } from "@tanstack/react-query";
import { getCmsContent, CMS_PATHS } from "../../adapters";
import { loggerService } from "../../observability";
import { QueryKeys } from "../QueryKeys";

export type LegalPageKey = LegalPage["key"];

export const useFetchLegalPage = (key: LegalPageKey, app: "care" | "portal" = "care", language?: LanguageKey) => {
  const getLegalPage = useCmsCopyStore(state => state.actions.getLegalPage);
  const cmsCopy = useCmsCopyStore(state => state.cmsCopy);
  const effectiveLanguage = language ?? "en";

  return useQuery({
    queryKey: [QueryKeys.fetchLegalPage, key, effectiveLanguage],
    queryFn: async () => {
      const result = await getCmsContent<
        LegalPage | undefined,
        [LegalPageKey, "care" | "portal", LanguageKey | undefined]
      >(getLegalPage, key, app, effectiveLanguage);

      if (!result) {
        loggerService.error(
          new Error("No legal page content found"),
          JSON.stringify({
            key,
            effectiveLanguage,
            availableStories: Object.keys(cmsCopy!.otherStories).filter(k =>
              k.startsWith(CMS_PATHS.LEGAL_PAGE[app]),
            ),
          }),
          {
            level: "error",
          },
        );
        throw new Error(`No legal page content found for key: ${key} and language: ${effectiveLanguage}`);
      }

      return result;
    },
    enabled: !!key && !!cmsCopy?.otherStories,
    retry: 3,
    retryDelay: 500,
  });
};
